<script lang="ts">
import { defineComponent, inject, ref, watch } from "vue";
import Infotext from "@/components/Infotext.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import formValidierung from "@/businesslogic/formValidierung";
import Zeile from "@/components/grid/Zeile.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Zelle from "@/components/grid/Zelle.vue";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "GewerbeinformationenMakler",
  components: {
    ValidierungsFehler,
    Infotext,
    Zeile,
    TitelZelle,
    Zelle,
  },
  props: {
    gewerbeId: Number,
    fehler: Object,
  },
  setup(props) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");

    const { erzwingeGanzzahl } = formValidierung();

    const gewerbeflaecheMitId = ref("gewerbeflaeche" + props.gewerbeId);
    const gewerbeArtMitId = ref("gewerbeArt" + props.gewerbeId);
    const gewerbeBeschreibungMitId = ref("gewerbeBeschreibungMitId" + props.gewerbeId);

    const GEWERBEART_SONSTIGE = "99000";

    const GEWERBEARTEN = [
      { art: "15310", beschreibung: "Büro / Kanzlei" },
      { art: "15330", beschreibung: "Geld-, Kreditinstitute" },
      { art: "15050", beschreibung: "Arztpraxis, Heilberufe" },
      { art: "15040", beschreibung: "Zahntechnischer Betrieb, Dentallabor" },
      { art: "15070", beschreibung: "Medizinisches Massageinstitut" },
      { art: "16230", beschreibung: "Sportstudio, Fitnesscenter" },
      { art: "15090", beschreibung: "Körperpflegebetrieb, Kosmetiksalon" },
      { art: "17410", beschreibung: "Apotheke" },
      { art: "17420", beschreibung: "Parfümerie, Drogerie" },
      { art: "17010", beschreibung: "Lebensmittelhandel" },
      { art: "17040", beschreibung: "Getränkehandel" },
      { art: "17020", beschreibung: "Bäckerei, Backwarenhandel" },
      { art: "17030", beschreibung: "Fleischerei, Fleischwarenhandel" },
      { art: "16040", beschreibung: "Imbiss, Kiosk" },
      { art: "17660", beschreibung: "Blumen-, Pflanzenhandel" },
      { art: "16030", beschreibung: "Gaststätte, Restaurant" },
      { art: "15010", beschreibung: "Friseur" },
      { art: GEWERBEART_SONSTIGE, beschreibung: "Sonstiges Gewerbe" },
    ];

    function setzteBeschreibungImStore(gewerbeListeElement: any) {
      if (gewerbeListeElement.art !== GEWERBEART_SONSTIGE) {
        for (const gewerbeart of GEWERBEARTEN) {
          if (gewerbeart.art === gewerbeListeElement.art) {
            gewerbeListeElement.beschreibung = gewerbeart.beschreibung;
          }
        }
      }
    }

    watch(
      () => [
        piniaStore.state.gebaeudedaten.gewerbeListe[0].art,
        piniaStore.state.gebaeudedaten.gewerbeListe[1].art,
        piniaStore.state.gebaeudedaten.gewerbeListe[2].art,
        piniaStore.state.gebaeudedaten.gewerbeListe[3].art,
      ],
      () => {
        for (const gewerbeListeElement of piniaStore.state.gebaeudedaten.gewerbeListe) {
          setzteBeschreibungImStore(gewerbeListeElement);
        }
      },
    );

    return {
      piniaStore,
      istSichtbar,
      wechsleSichtbarkeit,
      gewerbeflaecheMitId,
      gewerbeArtMitId,
      gewerbeBeschreibungMitId,
      erzwingeGanzzahl,
      GEWERBEARTEN,
      GEWERBEART_SONSTIGE,
    };
  },
});
</script>

<template>
  <zeile>
    <titel-zelle>
      <strong>{{ gewerbeId }}. Gewerbe-Art</strong>
    </titel-zelle>
    <zelle>
      <select
        :id="gewerbeArtMitId"
        :name="gewerbeArtMitId"
        v-model.lazy.trim="piniaStore.state.gebaeudedaten.gewerbeListe[gewerbeId - 1].art"
        class="c-form-control-select"
        data-fehlermeldung="Bitte wählen Sie die Art des Gewerbes aus."
        required
        aria-required="true"
        aria-label="Art des Gewerbes"
        data-cy="gewerbeGewerbeArt"
      >
        <template v-for="gewerbeart in GEWERBEARTEN" v-bind:key="gewerbeart.art">
          <option :value="gewerbeart.art">{{ gewerbeart.beschreibung }}</option>
        </template>
      </select>
      <validierungs-fehler :fehler-text="fehler[gewerbeArtMitId]" :reduzierter-abstand="true" />
    </zelle>
  </zeile>

  <zeile v-if="piniaStore.state.gebaeudedaten.gewerbeListe[gewerbeId - 1].art === GEWERBEART_SONSTIGE">
    <titel-zelle>
      <strong>Beschreibung {{ gewerbeId }}. Gewerbe</strong>
    </titel-zelle>
    <zelle>
      <input
        :id="gewerbeBeschreibungMitId"
        :name="gewerbeBeschreibungMitId"
        v-model.lazy.trim="piniaStore.state.gebaeudedaten.gewerbeListe[gewerbeId - 1].beschreibung"
        class="c-form-control__input"
        placeholder="Beschreibung des Gewerbes"
        minlength="3"
        maxlength="70"
        data-fehlermeldung="Bitte beschreiben Sie die Art des sonstigen Gewerbes (3-70 Zeichen)."
        required
        aria-required="true"
        aria-label="Beschreibung des Gewerbes"
        data-cy="gewerbeGewerbeArtSonstigesBeschreibung"
      />
      <validierungs-fehler :fehler-text="fehler[gewerbeBeschreibungMitId]" />
    </zelle>
  </zeile>

  <zeile>
    <titel-zelle :zeige-info="true" :info-box-id="gewerbeflaecheMitId">
      <strong>{{ gewerbeId }}. Anteil Gewerbefläche</strong>
      in %
    </titel-zelle>
    <zelle>
      <input
        type="number"
        @keydown="erzwingeGanzzahl"
        :name="gewerbeflaecheMitId"
        v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.gewerbeListe[gewerbeId - 1].flaeche"
        class="c-form-control__input"
        min="1"
        max="49"
        data-fehlermeldung="Überwiegend gewerblich genutzte Gebäude können wir nicht versichern."
        data-cy="gewerbeGewerbeFlaeche"
        required
        aria-required="true"
        aria-label="Anteil an Gewerbefläche"
        :aria-describedby="gewerbeArtMitId"
      />
      <validierungs-fehler :fehler-text="fehler[gewerbeflaecheMitId]" />
      <infotext
        role="tooltip"
        :id="gewerbeArtMitId"
        :name="gewerbeflaecheMitId"
        text="Anteil der gewerblich genutzten Fläche in Prozent von der Gesamtfläche des Gebäudes."
      >
      </infotext>
    </zelle>
  </zeile>
</template>
